import { useState, useMemo, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  MenuItem,
  FormControl,
  Select,
  Button,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Autocomplete,
  TextField,
  Checkbox,
  ListItemText,
  IconButton,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers';
import { useTranslation } from 'react-i18next';
import dayjs, { type Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import DownloadIcon from '@mui/icons-material/Download';
import MapIcon from '@mui/icons-material/Map';
import { useAppSelector, useAppDispatch } from 'store/hook';
import { selectAssets } from 'store/asset.slice';
import { showNotification } from 'store/notification.slice';
import useAppTheme from 'hooks/useAppTheme';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import useCustomerInfo from 'hooks/useCustomerInfo';
import {
  resolveShiftOptions,
  resolveCurrentShiftIndex,
} from 'utils/helpers/customerShiftHelpers';
import CustomerShiftConfigAPI from 'api/customer-shift-config.api';
import {
  setCustomerShiftConfigData,
  setCustomerShiftConfigLoading,
} from 'store/customer-shift-config.slice';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';
import ReportAPI from 'api/report.api';
import { LocationMapModal } from 'views/DispatchImporters/LocationMapModal';
import type { LocationRecord } from 'views/DispatchImporters/LocationImporterView';

dayjs.extend(utc);
dayjs.extend(timezone);

const LIVE_POLL_INTERVAL_MS = 5 * 60 * 1000;

// dump locations that aren't a real mapped location — never plottable on the map
const EXCLUDED_DUMP_VALUES = new Set(['na', 'other']);
const isExcludedDump = (dump: unknown): boolean =>
  EXCLUDED_DUMP_VALUES.has(
    String(dump ?? '')
      .trim()
      .toLowerCase()
  );

// Same label the Location column renders (dump name, or "Route" for a line,
// or lat/lon for a bare point) — used for both display and sorting so the
// column sorts consistently with what's actually shown.
const getLocationLabel = (row: any): string => {
  const geom = row.geojson;
  const isLine =
    geom?.type === 'LineString' &&
    Array.isArray(geom.coordinates) &&
    geom.coordinates.length > 0;
  const isPoint =
    geom?.type === 'Point' &&
    Array.isArray(geom.coordinates) &&
    geom.coordinates.length >= 2;

  const anchor: [number, number] | null = isLine
    ? geom.coordinates[Math.floor(geom.coordinates.length / 2)]
    : isPoint
    ? geom.coordinates
    : null;
  if (!anchor) return '';

  const [anchorLon, anchorLat] = anchor;
  const dumpName = row.dump?.trim();
  return (
    dumpName ||
    (isLine ? 'Route' : `${anchorLat.toFixed(4)}, ${anchorLon.toFixed(4)}`)
  );
};

// Sort key for the Location column: named locations (dump names, "Route")
// before bare coordinate labels ("28.6139, 77.2090"), and rows with no
// location at all last — rather than a plain string sort, which puts
// digit-first coordinate labels ahead of any letter.
const getLocationSortKey = (row: any): [number, string] => {
  const label = getLocationLabel(row);
  if (!label) return [2, ''];
  return [/^[0-9]/.test(label) ? 1 : 0, label];
};

const formatTripTime = (value: unknown, tz: string): string =>
  value
    ? dayjs
        .utc(value as string)
        .tz(tz)
        .format('DD/MM/YY HH:mm')
    : '';

const downloadCsv = (rows: any[], filename: string, tz: string) => {
  const headers = ['Truck', 'Time Full', 'Time Empty', 'Dump', 'Tonnage'];
  const csvRows = [
    headers.join(','),
    ...rows.map((r) =>
      [
        r.truck ?? '',
        formatTripTime(r.timeFull, tz),
        formatTripTime(r.timeEmpty, tz),
        r.dump ?? '',
        r.computedTonnage != null ? Number(r.computedTonnage).toFixed(2) : '',
      ]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(',')
    ),
  ];
  const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const TripReportSensor = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { timezone: tz } = useTimeDisplayMode();
  const { customerCode, customerTz } = useCustomerInfo();
  const reportApi = useRef(new ReportAPI()).current;

  const customerId = useAppSelector(
    (state) => state.persistedReducer.customer.id
  );
  const shiftPeriods = useAppSelector(
    (state) => state.customerShiftConfigReducer.data?.shiftPeriods ?? []
  );
  const shiftConfigCustCode = useAppSelector(
    (state) => state.customerShiftConfigReducer.custCode
  );
  const shiftConfigLoading = useAppSelector(
    (state) => state.customerShiftConfigReducer.loading
  );

  // The shift config is normally only fetched when a user switches customers
  // via the top-bar CustomerSelector — arriving here directly (deep link,
  // page refresh with a persisted customer) can leave it empty, so "current
  // shift" can never resolve. Load it ourselves whenever it's missing or
  // stale for the current customer. Fire-and-forget: only dispatches into
  // Redux, never blocks or delays the Live fetch below.
  useEffect(() => {
    if (!customerCode || shiftConfigLoading) return;
    if (shiftConfigCustCode?.toLowerCase() === customerCode.toLowerCase())
      return;

    let cancelled = false;
    dispatch(setCustomerShiftConfigLoading(true));
    CustomerShiftConfigAPI.getCustomerShiftConfig(customerCode)
      .then((shiftConfig) => {
        if (cancelled) return;
        dispatch(
          setCustomerShiftConfigData({
            custCode: customerCode,
            data: shiftConfig,
          })
        );
      })
      .catch((err) => {
        console.error('Failed to load customer shift config', err);
        if (!cancelled) dispatch(setCustomerShiftConfigLoading(false));
      });

    return () => {
      cancelled = true;
    };
  }, [customerCode, shiftConfigCustCode, shiftConfigLoading, dispatch]);

  const today = dayjs().format('YYYY-MM-DD');
  const [mode, setMode] = useState<'live' | 'single' | 'range'>('live');
  const [isPolling, setIsPolling] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null);
  const [liveShiftIndex, setLiveShiftIndex] = useState<number | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isMountedRef = useRef(true);
  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    []
  );

  const [date, setDate] = useState<string>(today);
  // One or more shifts may be selected; at least one is required to fetch.
  const [shifts, setShifts] = useState<string[]>([]);

  const [rangeStart, setRangeStart] = useState<string>('');
  const [rangeEnd, setRangeEnd] = useState<string>('');

  const [rows, setRows] = useState<any[]>([]);

  // Client-side filters — applied to already-fetched rows only
  const [truckFilter, setTruckFilter] = useState<string | null>(null);
  const [dumpFilter, setDumpFilter] = useState<string | null>(null);

  const assets = useAppSelector(selectAssets);

  const uniqueDumps = useMemo(
    () =>
      Array.from(new Set(rows.map((r) => r.dump).filter(Boolean))).sort(
        (a, b) => (a as string).localeCompare(b as string)
      ) as string[],
    [rows]
  );

  // Truck options — all assets for the logged-in customer (bumper numbers),
  // from the Redux asset slice.
  const truckOptions = useMemo(() => {
    const set = new Set<string>();
    assets.forEach((a) => {
      if (a.bumperNumber) set.add(a.bumperNumber);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [assets]);

  // The API's `truck` field is the tracker device id (e.g. "symbotE401242"),
  // not the human-readable bumper number shown in the Truck filter. Map it
  // to bumper number so the displayed value and the filter match the same
  // identifier space.
  const deviceIdToBumper = useMemo(() => {
    const map = new Map<string, string>();
    assets.forEach((a) => {
      if (a.device?.deviceId && a.bumperNumber) {
        map.set(a.device.deviceId, a.bumperNumber);
      }
    });
    return map;
  }, [assets]);

  const getBumperNumber = (rawTruck: string | null | undefined): string =>
    (rawTruck ? deviceIdToBumper.get(rawTruck) : undefined) ?? rawTruck ?? '';

  // Rows shown in the grid / exported to CSV after applying all filters.
  // The filters compose independently — order of selection doesn't matter,
  // and clearing one keeps the others applied.
  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      const truckOk =
        !truckFilter ||
        getBumperNumber(r.truck).toLowerCase() === truckFilter.toLowerCase();
      const dumpOk =
        !dumpFilter || r.dump?.toLowerCase() === dumpFilter.toLowerCase();
      return truckOk && dumpOk;
    });
  }, [rows, truckFilter, dumpFilter, deviceIdToBumper]);

  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [lastGeneratedDate, setLastGeneratedDate] = useState<string | null>(
    null
  );

  const shiftOptions =
    shiftPeriods.length > 0
      ? shiftPeriods
      : [{ shiftName: 'Day Shift' }, { shiftName: 'Night Shift' }];

  const inputOutlineSx = {
    '& .MuiOutlinedInput-root': {
      '& fieldset': { borderColor: palette.borderGrey },
      '&:hover fieldset': { borderColor: palette.borderGrey },
      '&.Mui-focused fieldset': {
        borderColor: palette.borderGrey,
        borderWidth: 1.5,
      },
      '& .MuiInputBase-input': { color: 'text.primary' },
      '& .MuiSvgIcon-root': { color: 'text.primary' },
    },
    '& .MuiInputLabel-root': {
      color: 'text.secondary',
      '&.Mui-focused': { color: 'text.primary' },
    },
    '& .MuiInputBase-root': { height: 40 },
  } as const;

  const isRangeDayDisabled = (day: Dayjs): boolean => {
    const dayStr = day.format('YYYY-MM-DD');
    const monthAgo = dayjs().subtract(1, 'month').startOf('day');
    if (day.isBefore(monthAgo)) return true;
    if (day.isAfter(dayjs())) return true;
    if (dayStr === today && lastGeneratedDate !== today) return true;
    return false;
  };

  // Every fetch — Single Date, Date Range, and Live — goes through this one
  // call so Live issues the exact same request shape as Single Date, just
  // with `date`/`shift` computed automatically instead of picked by hand.
  const runFetch = async (
    payload: {
      date?: string;
      dateFrom?: string;
      dateTo?: string;
      shift: string[];
    },
    options: { silent?: boolean; isLive?: boolean } = {}
  ) => {
    if (!customerId) return;
    const { silent = false, isLive = false } = options;

    if (silent) setIsPolling(true);
    else setLoading(true);

    try {
      const data = (await reportApi.getTripReportSensor({
        customerId,
        ...payload,
      })) as any[];

      if (!isMountedRef.current) return;
      setRows(data.map((r: any, i: number) => ({ ...r, _gridId: r.id ?? i })));

      if (!silent) {
        setTruckFilter(null);
        setDumpFilter(null);
      }

      if (data.length > 0) {
        const latest = data.reduce((max: string, r: any) => {
          const d = r.date ?? '';
          return d > max ? d : max;
        }, '');
        if (latest) setLastGeneratedDate(latest);
      }

      if (isLive) setLastUpdatedAt(Date.now());
      setFetched(true);
    } catch (err) {
      console.error('Failed to fetch trip report (sensor)', err);
      const message = await ReportAPI.extractErrorMessage(err);
      if (isMountedRef.current) {
        dispatch(showNotification({ message, type: 'error' }));
      }
    } finally {
      if (isMountedRef.current) {
        if (silent) setIsPolling(false);
        else setLoading(false);
      }
    }
  };

  const handleFetch = async () => {
    if (mode === 'single' && (!date || shifts.length === 0)) return;
    if (mode === 'range' && (!rangeStart || !rangeEnd || shifts.length === 0))
      return;

    if (mode === 'single') {
      await runFetch({ date, shift: shifts });
    } else {
      await runFetch({ dateFrom: rangeStart, dateTo: rangeEnd, shift: shifts });
    }
  };

  // Live mode: resolve today's date (customer timezone) and whichever shift is
  // currently active, then call `runFetch` the same way Single Date does.
  // `silent` refreshes never toggle the blocking `loading` flag — the grid
  // keeps showing the last-known rows while the request is in flight, so the
  // screen never appears to hang.
  const fetchLive = async (silent: boolean) => {
    if (!customerTz) return;

    const todayInCustomerTz = dayjs().tz(customerTz).format('YYYY-MM-DD');
    const boundaryOptions = resolveShiftOptions(
      shiftPeriods,
      customerCode ?? ''
    );
    const activeIndex = resolveCurrentShiftIndex(boundaryOptions, customerTz);

    const currentShiftName =
      activeIndex !== null ? shiftOptions[activeIndex]?.shiftName : undefined;
    const liveShifts = currentShiftName
      ? [currentShiftName]
      : shiftOptions.map((s) => s.shiftName);

    setLiveShiftIndex(activeIndex);
    setDate(todayInCustomerTz);
    setShifts(liveShifts);

    await runFetch(
      { date: todayInCustomerTz, shift: liveShifts },
      { silent, isLive: true }
    );
  };

  useEffect(() => {
    if (mode !== 'live') {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      return;
    }

    void fetchLive(false);
    pollIntervalRef.current = setInterval(() => {
      void fetchLive(true);
    }, LIVE_POLL_INTERVAL_MS);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [mode, customerId, customerCode, customerTz, shiftPeriods.length]);

  const [mapOpen, setMapOpen] = useState(false);
  const [mapLocation, setMapLocation] = useState<LocationRecord | null>(null);
  const [mapRouteGeojson, setMapRouteGeojson] = useState<{
    type: string;
    coordinates: unknown;
  } | null>(null);
  const [mapMultiPoints, setMapMultiPoints] = useState<
    Array<{ id: string; name: string; lat: number; lon: number }>
  >([]);
  const [mapMultiRoutes, setMapMultiRoutes] = useState<
    Array<{ id: string; coordinates: Array<[number, number]> }>
  >([]);

  const handleViewAllLocations = () => {
    const points: Array<{
      id: string;
      name: string;
      lat: number;
      lon: number;
    }> = [];
    const routes: Array<{ id: string; coordinates: Array<[number, number]> }> =
      [];

    filteredRows.forEach((row) => {
      if (isExcludedDump(row.dump)) return;
      const geom = row.geojson;
      const rowId = String(
        row.id ?? row._gridId ?? `${row.truck}-${row.timeFull}`
      );
      if (
        geom?.type === 'Point' &&
        Array.isArray(geom.coordinates) &&
        geom.coordinates.length >= 2
      ) {
        const [lon, lat] = geom.coordinates;
        points.push({
          id: rowId,
          name: getBumperNumber(row.truck) || 'Location',
          lat,
          lon,
        });
      } else if (
        geom?.type === 'LineString' &&
        Array.isArray(geom.coordinates) &&
        geom.coordinates.length > 0
      ) {
        routes.push({ id: rowId, coordinates: geom.coordinates });
      }
    });

    setMapLocation(null);
    setMapRouteGeojson(null);
    setMapMultiPoints(points);
    setMapMultiRoutes(routes);
    setMapOpen(true);
  };

  const columns: GridColDef[] = [
    {
      field: 'truck',
      headerName: 'Truck',
      width: 100,
      valueFormatter: ({ value }: { value: unknown }) =>
        getBumperNumber(value as string),
    },
    {
      field: 'timeFull',
      headerName: 'Time Full',
      width: 140,
      valueFormatter: ({ value }: { value: unknown }) =>
        formatTripTime(value, tz),
    },
    {
      field: 'timeEmpty',
      headerName: 'Time Empty',
      width: 140,
      valueFormatter: ({ value }: { value: unknown }) =>
        formatTripTime(value, tz),
    },
    { field: 'dump', headerName: 'Dump', width: 110 },
    {
      field: 'computedTonnage',
      headerName: 'Tonnage',
      width: 100,
      type: 'number',
      valueFormatter: ({ value }: { value: unknown }) =>
        value != null ? Number(value).toFixed(2) : '',
    },
    {
      field: 'geojson',
      headerName: 'Location',
      width: 170,
      filterable: false,
      sortComparator: (_v1, _v2, param1, param2) => {
        const [rank1, label1] = getLocationSortKey(
          param1.api.getRow(param1.id)
        );
        const [rank2, label2] = getLocationSortKey(
          param2.api.getRow(param2.id)
        );
        return rank1 !== rank2 ? rank1 - rank2 : label1.localeCompare(label2);
      },
      renderCell: ({ row }) => {
        const geom = row.geojson;
        const isLine =
          geom?.type === 'LineString' &&
          Array.isArray(geom.coordinates) &&
          geom.coordinates.length > 0;
        const isPoint =
          geom?.type === 'Point' &&
          Array.isArray(geom.coordinates) &&
          geom.coordinates.length >= 2;

        const anchor: [number, number] | null = isLine
          ? geom.coordinates[Math.floor(geom.coordinates.length / 2)]
          : isPoint
          ? geom.coordinates
          : null;
        if (!anchor) return null;

        const [anchorLon, anchorLat] = anchor;
        const label = getLocationLabel(row);
        const excludedDump = isExcludedDump(row.dump);

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography fontSize={12}>📍 {label}</Typography>
            <IconButton
              size="small"
              disabled={excludedDump}
              onClick={() => {
                setMapMultiPoints([]);
                setMapMultiRoutes([]);
                setMapLocation({
                  id: row.id ?? row._gridId ?? `${row.truck}-${row.timeFull}`,
                  name: getBumperNumber(row.truck) || 'Location',
                  locationType: 'trip',
                  easting: 0,
                  northing: 0,
                  elevation: 0,
                  active: true,
                  centroidLat: anchorLat,
                  centroidLon: anchorLon,
                });
                setMapRouteGeojson(isLine ? geom : null);
                setMapOpen(true);
              }}
              sx={{ p: 0.25 }}
            >
              <MapIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  const canFetch =
    mode === 'single'
      ? Boolean(date && shifts.length > 0 && !loading)
      : Boolean(rangeStart && rangeEnd && shifts.length > 0 && !loading);

  const csvFilename =
    mode === 'live'
      ? `trip-report-sensor-live-${dayjs().format('YYYY-MM-DD-HHmmss')}.csv`
      : mode === 'single'
      ? `trip-report-sensor-${date}-${shifts
          .map((s) => s.replace(/\s*shift\s*/i, '').trim())
          .join('-')} Shift.csv`
      : `trip-report-sensor-${rangeStart}-to-${rangeEnd}.csv`;

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          bgcolor: 'background.paper',
          width: { xs: '100%', md: '80%' },
          flexBasis: { md: '80%' },
          flexGrow: 1,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, color: 'text.primary', mb: 2.5 }}
        >
          {t('trip_report_sensor_title')}
        </Typography>

        <Box sx={{ mb: 2 }}>
          <ToggleButtonGroup
            value={mode}
            exclusive
            size="small"
            onChange={(_, val) => {
              if (!val) return;
              setMode(val);
              setFetched(false);
              setRows([]);
              setTruckFilter(null);
              setDumpFilter(null);
            }}
            sx={{
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                px: 2,
                fontSize: '0.8rem',
                color: 'text.secondary',
                borderColor: palette.borderGrey,
                '&.Mui-selected': {
                  color: 'text.primary',
                  bgcolor: enableDarkTheme
                    ? 'rgba(255,255,255,0.08)'
                    : 'rgba(0,0,0,0.06)',
                },
              },
            }}
          >
            <ToggleButton value="live">
              {t('trip_report_sensor_live_tab')}
            </ToggleButton>
            <ToggleButton value="single">{t('single_date')}</ToggleButton>
            <ToggleButton value="range">{t('date_range')}</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Stack
          direction="row"
          alignItems="flex-end"
          sx={{ mb: 3, flexWrap: 'wrap', gap: 2 }}
        >
          {mode === 'live' ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                height: 40,
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: 'success.main',
                  animation:
                    'trip-report-sensor-live-pulse 2s ease-in-out infinite',
                  '@keyframes trip-report-sensor-live-pulse': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.35 },
                  },
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  color: 'success.main',
                  textTransform: 'uppercase',
                }}
              >
                {t('trip_report_sensor_live')}
              </Typography>
              {liveShiftIndex !== null && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {t('trip_report_sensor_current_shift', {
                    shift:
                      liveShiftIndex === 0
                        ? t('shift_day')
                        : liveShiftIndex === 1
                        ? t('shift_night')
                        : t('trip_report_sensor_shift_n', {
                            n: liveShiftIndex + 1,
                          }),
                  })}
                </Typography>
              )}
              {lastUpdatedAt && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {t('trip_report_sensor_last_updated', {
                    time: dayjs(lastUpdatedAt)
                      .tz(customerTz || dayjs.tz.guess())
                      .format('HH:mm:ss'),
                  })}
                </Typography>
              )}
              {isPolling && <CircularProgress size={14} />}
            </Box>
          ) : mode === 'single' ? (
            <>
              <Box>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}
                >
                  {t('date')}
                </Typography>
                <DatePicker
                  value={date ? dayjs(date) : null}
                  maxDate={dayjs()}
                  onChange={(v) => {
                    setDate(v?.format('YYYY-MM-DD') ?? '');
                    setFetched(false);
                  }}
                  slotProps={{
                    textField: {
                      size: 'small',
                      inputProps: { readOnly: true },
                      sx: { ...inputOutlineSx, width: 180 },
                    },
                  }}
                />
              </Box>

              <Box>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}
                >
                  {t('shift')}
                </Typography>
                <FormControl
                  size="small"
                  sx={{ ...inputOutlineSx, width: 200 }}
                >
                  <Select
                    multiple
                    value={shifts}
                    displayEmpty
                    onChange={(e) => {
                      const val = e.target.value;
                      setShifts(typeof val === 'string' ? val.split(',') : val);
                      setFetched(false);
                    }}
                    renderValue={(selected) =>
                      selected.length === 0 ? (
                        <span style={{ color: '#aaa' }}>
                          {t('select_shifts')}
                        </span>
                      ) : (
                        selected.join(', ')
                      )
                    }
                  >
                    {shiftOptions.map((s) => (
                      <MenuItem key={s.shiftName} value={s.shiftName}>
                        <Checkbox checked={shifts.includes(s.shiftName)} />
                        <ListItemText primary={s.shiftName} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </>
          ) : (
            <>
              <Box>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}
                >
                  {t('from')}
                </Typography>
                <DatePicker
                  value={rangeStart ? dayjs(rangeStart) : null}
                  maxDate={rangeEnd ? dayjs(rangeEnd) : dayjs()}
                  minDate={dayjs().subtract(1, 'month').startOf('day')}
                  shouldDisableDate={isRangeDayDisabled}
                  onChange={(v) => {
                    setRangeStart(v?.format('YYYY-MM-DD') ?? '');
                    setFetched(false);
                  }}
                  slotProps={{
                    textField: {
                      size: 'small',
                      inputProps: { readOnly: true },
                      sx: { ...inputOutlineSx, width: 160 },
                    },
                  }}
                />
              </Box>

              <Box>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}
                >
                  {t('to')}
                </Typography>
                <DatePicker
                  value={rangeEnd ? dayjs(rangeEnd) : null}
                  minDate={
                    rangeStart
                      ? dayjs(rangeStart)
                      : dayjs().subtract(1, 'month').startOf('day')
                  }
                  maxDate={
                    rangeStart
                      ? dayjs(rangeStart).add(1, 'month').isAfter(dayjs())
                        ? dayjs()
                        : dayjs(rangeStart).add(1, 'month')
                      : dayjs()
                  }
                  shouldDisableDate={isRangeDayDisabled}
                  onChange={(v) => {
                    setRangeEnd(v?.format('YYYY-MM-DD') ?? '');
                    setFetched(false);
                  }}
                  slotProps={{
                    textField: {
                      size: 'small',
                      inputProps: { readOnly: true },
                      sx: { ...inputOutlineSx, width: 160 },
                    },
                  }}
                />
              </Box>

              <Box>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}
                >
                  {t('shift')}
                </Typography>
                <FormControl
                  size="small"
                  sx={{ ...inputOutlineSx, width: 200 }}
                >
                  <Select
                    multiple
                    value={shifts}
                    displayEmpty
                    onChange={(e) => {
                      const val = e.target.value;
                      setShifts(typeof val === 'string' ? val.split(',') : val);
                      setFetched(false);
                    }}
                    renderValue={(selected) =>
                      selected.length === 0 ? (
                        <span style={{ color: '#aaa' }}>
                          {t('select_shifts')}
                        </span>
                      ) : (
                        selected.join(', ')
                      )
                    }
                  >
                    {shiftOptions.map((s) => (
                      <MenuItem key={s.shiftName} value={s.shiftName}>
                        <Checkbox checked={shifts.includes(s.shiftName)} />
                        <ListItemText primary={s.shiftName} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </>
          )}

          {mode !== 'live' && (
            <Button
              variant="contained"
              onClick={handleFetch}
              disabled={!canFetch}
              sx={{ textTransform: 'none', height: 40 }}
            >
              {loading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                t('fetch')
              )}
            </Button>
          )}

          <Box>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}
            >
              {t('truck')}
            </Typography>
            <Autocomplete
              size="small"
              disabled={!fetched}
              options={truckOptions}
              value={truckFilter}
              onChange={(_, val) => {
                setTruckFilter(val);
              }}
              sx={{ width: 190 }}
              slotProps={{
                paper: {
                  sx: { width: 'max-content', minWidth: 190, maxWidth: 320 },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t('all_trucks')}
                  sx={inputOutlineSx}
                />
              )}
            />
          </Box>

          <Box>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}
            >
              {t('dump')}
            </Typography>
            <Autocomplete
              size="small"
              disabled={!fetched || uniqueDumps.length === 0}
              options={uniqueDumps}
              value={dumpFilter}
              onChange={(_, val) => {
                setDumpFilter(val);
              }}
              sx={{ width: 190 }}
              slotProps={{
                paper: {
                  sx: { width: 'max-content', minWidth: 190, maxWidth: 320 },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t('all_dumps')}
                  sx={inputOutlineSx}
                />
              )}
            />
          </Box>
        </Stack>

        {mode === 'live' && loading && !fetched && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: 200,
            }}
          >
            <CircularProgress size={28} />
          </Box>
        )}

        {fetched && (
          <>
            <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<MapIcon sx={{ fontSize: 16 }} />}
                disabled={
                  !filteredRows.some(
                    (r) => r.geojson && !isExcludedDump(r.dump)
                  ) || filteredRows.length === 0
                }
                onClick={handleViewAllLocations}
                sx={{
                  textTransform: 'none',
                  borderColor: palette.borderGrey,
                  color: 'text.primary',
                  '&:hover': {
                    borderColor: palette.borderGrey,
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                View All Dumping Locations
              </Button>
            </Stack>

            <Box sx={{ height: 480, width: '100%' }}>
              {filteredRows.length === 0 ? (
                <Typography
                  variant="body2"
                  sx={{ color: 'text.secondary', mt: 2 }}
                >
                  No data found for the selected period.
                </Typography>
              ) : (
                <DataGrid
                  rows={filteredRows}
                  columns={columns}
                  getRowId={(r) => r._gridId ?? r.id}
                  pageSizeOptions={[25, 50, 100]}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 25 } },
                  }}
                  sx={{
                    border: `1px solid ${palette.borderGrey}`,
                    '& .MuiDataGrid-columnHeaders': {
                      bgcolor: 'background.default',
                    },
                  }}
                />
              )}
            </Box>

            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                disabled={filteredRows.length === 0}
                onClick={() => {
                  downloadCsv(
                    filteredRows.map((r) => ({
                      ...r,
                      truck: getBumperNumber(r.truck),
                    })),
                    csvFilename,
                    tz
                  );
                }}
                sx={{
                  textTransform: 'none',
                  borderColor: palette.borderGrey,
                  color: 'text.primary',
                  '&:hover': {
                    borderColor: palette.borderGrey,
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                Download CSV
              </Button>
            </Stack>
          </>
        )}
      </Paper>

      <LocationMapModal
        open={mapOpen}
        onClose={() => {
          setMapOpen(false);
          setMapLocation(null);
          setMapRouteGeojson(null);
          setMapMultiPoints([]);
          setMapMultiRoutes([]);
        }}
        location={mapLocation}
        showTrucks={false}
        routeGeojson={mapRouteGeojson}
        multiPoints={mapMultiPoints}
        multiRoutes={mapMultiRoutes}
        showRoadNetwork
        showAllLocations
        locationTypeFilter={['dumping', 'crusher', 'stockpile']}
        hideCoordinates
      />
    </>
  );
};

export default TripReportSensor;
